/**
 * How a golden-source intake value is PRESENTED to the customer (owner item 14, 2026-08-24:
 * *"Data is not looking aesthetic, we must enhance UI"*).
 *
 * <p>The screen this serves asks the customer to confirm that details someone else supplied about
 * them are correct. That only works if they can actually read them, and the card was showing raw
 * wire values — `1995-05-04` for a date of birth, an unbroken 10-digit phone number — centred in a
 * column with no hierarchy. A person checking their own date of birth should not have to parse an
 * ISO string to do it.
 *
 * <p><strong>Presentation only, and never a rewrite.</strong> Every function here is total and
 * falls back to the value EXACTLY as served the moment it is not confident: a value it cannot parse
 * is shown verbatim, never reformatted into a guess and never blanked. The customer is confirming
 * the record as held — so what they read must be the record, only legible.
 */

/** What kind of value a row carries, inferred from its template key. Presentation only. */
export type IntakeValueKind = 'DATE' | 'PHONE' | 'EMAIL' | 'TEXT';

/**
 * Classify a row by its template key. Keys are tenant-authored, so this matches on the shape of the
 * name rather than a closed list — and anything unrecognised is TEXT, which renders verbatim.
 */
export function intakeValueKind(key: string): IntakeValueKind {
  const k = key.toLowerCase();
  if (/(^|_)(dob|date_of_birth|birth_date|birthdate)($|_)/.test(k) || k.includes('date_of_birth')) {
    return 'DATE';
  }
  if (k.includes('phone') || k.includes('mobile') || k.includes('msisdn')) return 'PHONE';
  if (k.includes('email')) return 'EMAIL';
  return 'TEXT';
}

/**
 * An ISO `YYYY-MM-DD` rendered in the reader's locale ("4 May 1995" / "4 मई 1995").
 *
 * <p>Parsed as PLAIN CALENDAR PARTS, deliberately — `new Date('1995-05-04')` is UTC midnight, which
 * in any timezone behind UTC renders as the 3rd. A date of birth shown a day early on a screen that
 * asks "is this correct?" would be the system inventing a discrepancy for the officer to chase.
 */
export function formatIntakeDate(value: string, locale: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!m) return value;
  const [, y, mo, d] = m;
  const year = Number(y);
  const month = Number(mo);
  const day = Number(d);
  if (month < 1 || month > 12 || day < 1 || day > 31) return value;
  try {
    // Noon local: far from either midnight boundary, so no locale can shift the calendar day.
    const dt = new Date(year, month - 1, day, 12);
    if (Number.isNaN(dt.getTime()) || dt.getMonth() !== month - 1 || dt.getDate() !== day) {
      return value; // a date the calendar does not have (31 Feb) — show what was served
    }
    return new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(dt);
  } catch {
    return value;
  }
}

/**
 * Group an Indian mobile number for reading: `9472938218` → `94729 38218`.
 *
 * <p>Grouped with a NON-BREAKING space so the number never wraps mid-way down a narrow phone screen,
 * and left alone entirely unless it is exactly ten digits — an international number, an extension or
 * anything already formatted is shown as served rather than mangled into a shape it is not.
 */
export function formatIntakePhone(value: string): string {
  const trimmed = value.trim();
  if (!/^\d{10}$/.test(trimmed)) return trimmed;
  return `${trimmed.slice(0, 5)} ${trimmed.slice(5)}`;
}

/** Present one intake value for reading. Unknown kinds and unparseable values pass through. */
export function formatIntakeValue(key: string, value: string, locale: string): string {
  switch (intakeValueKind(key)) {
    case 'DATE':
      return formatIntakeDate(value, locale);
    case 'PHONE':
      return formatIntakePhone(value);
    default:
      return value;
  }
}
